<template>
  <div class="emoji-reactions-container">
    <div
      v-for="(reaction, index) in reactionList" :key="index"
      @click="() => handleReactionClick(reaction)"
      :class="{ selected : userReactions.includes(reaction) }"
      class="emoji-container"
      :data-testid="`viewer-emoji-reaction-${reaction}`"
    >
      <span>{{ emoji.get(reaction)}}</span>
      <span class="reaction-count">{{ retrieveReactionCount(reaction) }}</span>
    </div>
  </div>
</template>

<script>
const emoji = require('node-emoji');
import track from '@/src/tracking';
import { mapState } from 'vuex';
import { camelCase, startCase } from 'lodash';

export default {
  props: {
    file_extension: { type: String, default: "" }, // only present for type: Item (ex. ".webm")
    file_name: { type: String, default: "" }, // only present for type: Item (ex. "24d6f6c4-5f42-4a5b-9d2e-5638a6efc345")
    id: { type: String, required: true }, // id of the thing with reactions (item, comment, whatever)
    item_thumbnail_url:  { type: String, default: "" }, // only present for type: Item
    item_type:  { type: String, default: "" }, // only present for type: Item (ex. "video")
    name:  { type: String, default: "" }, // name of the thing with reactions (item, comment, whatever)
    owner: { type: Object, default: () => {} }, // the user who the reactable thing belongs to
    reactions: { type: Array, default: () => [] }, // reactions that exist on the item, comment, whatever
    share_url: { type: String, default: "" },
    type: { type: String, required: true } // type of the thing with reactions (item, comment, whatever)
  },
  data: function(){
    return {
      emoji: emoji,
      reactionList: ["thumbsup", "thumbsdown", "fire", "heart"],
      userReactions: [], // to track if the user has clicked on the reaction or not
    }
  },
  computed: {
    ...mapState({
      currentUser: state => state.user,
    }),
    formattedType() {
      return startCase(camelCase(this.type)).replace(/ /g, ''); // reactable table wants model like (Item) type names
    },
    reactionCount() {
      if (!this.translatedReactions || !this.translatedReactions.length) return 0;
      return this.translatedReactions.reduce((accum, curr) => {
        return accum += curr.count;
      }, 0);
    },
    translatedReactions() {
      return this.reactions.map((r) => ({...r.attributes}));
    }
  },
  methods: {
    doTracking(r) {
      const payload = {
        anonymous: !this.currentUser?.id,
        emoji: r,
        file_extension: this.file_extension,
        file_type: this.item_type,
        file_name: this.file_name,
        item_hash_id: this.id,
        name: this.name,
        owner_email: this.owner?.meta?.email,
        owner_id: this.owner?.data?.id,
        public_url: this.share_url,
        reaction_count: this.reactionCount + 1, // race condition, this.reactionCount updates late
        reactable_id: this.id,
        reactable_type: this.formattedType,
        thumbnail_url: this.item_thumbnail_url,
        user_agent: navigator.userAgent,
      };
      track("viewer_emoji_clicked", payload);
    },
    handleAddReaction(r) {
      const payload = this.preparePayload(r);
      // if/when support for reactions on something other than items is needed
      // logic to call type specific actions can go here
      this.$store.dispatch("addItemReaction", payload);
      this.userReactions.push(r);
    },
    handleReactionClick(r) {
      this.doTracking();
      // did the user already click this reaction?
      if (this.userReactions.includes(r)) {
        // if so then remove it
        this.handleRemoveReaction(r);
      } else {
        // if not, then add it
        this.handleAddReaction(r);
      }
    },
    handleRemoveReaction(r) {
      const reactionFromName = this.reactions.find((n) => n.attributes.name === r);
      if (!reactionFromName) return;
      // if/when support for reactions on something other than items is needed
      // logic to call type specific actions can go here
      this.$store.dispatch("removeItemReaction", {
        reactionId: reactionFromName.id, // the hashid of the reaction
        itemId: this.id, // the hashid of the item
      });
      this.userReactions = this.userReactions.filter((prevReaction) => prevReaction !== r);
    },
    preparePayload(r) {
      const reactionOnItem = this.translatedReactions.find((rn) => rn.name === r);
      if (reactionOnItem) {
        return reactionOnItem;
      }
      return {
        name: r,
        reactable_id: this.id,
        reactable_type: this.formattedType,
        count: 0,
      };
    },
    retrieveReactionCount(reaction) {
      const reactionFound = this.translatedReactions.find((r) => r.name === reaction);
      return reactionFound ? reactionFound.count : 0;
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/packs/colors.scss';

  .emoji-reactions-container {
    display: flex;
    justify-content: flex-end;

    span {
      font-size: 1.15rem;
    }

    .emoji-container {
      align-items: center;
      background-color: $white;
      border-radius: 12px;
      cursor: pointer;
      display: flex;
      padding: 0.125rem 1rem;
      margin-left: 15px;
    }

    .selected {
      background-color: $bright-blue;

      span {
        color: $white;
      }
    }

    .reaction-count {
      color: $c-dark-gray;
      font-size: 0.75rem;
      font-weight: bold;
      margin-left: 7px;
    }
  }
</style>
